// Screening logic (04_screen_screening.md) — verdict ก่อนสั่งซื้อ
import { reqLabel, reqCat } from './reqtypes.js';
import { measureAgencies, PERMIT_AGENCY } from './permit-flags.js';

const AGENCY_TH = {
  forest_dept: 'กรมป่าไม้', tisi: 'สมอ. (มอก.)', fda: 'อย.', excise: 'กรมสรรพสามิต',
  customs: 'ศุลกากร', dft: 'กรมการค้าต่างประเทศ', other: 'หน่วยงานอื่น',
};

export function verdictOf(rule, product) {
  if (!rule) return {
    level: 'unknown', label: 'ยังไม่มีข้อมูลพิกัด',
    color: 'var(--muted)', reason: 'ยังไม่มีแถวใน hs_rule — ทำ dossier ให้ broker ตีพิกัดก่อน',
  };
  if (rule.is_prohibited) return {
    level: 'hold', label: 'ติดข้อห้าม', color: 'var(--c-customs)',
    reason: 'สินค้าอยู่ในบัญชีต้องห้ามนำเข้า — ห้ามสั่ง',
  };
  const measures = Array.isArray(rule.measures) ? rule.measures : [];
  const hsUnconfirmed = product && product.hs_confirmed === false;
  if (measures.length > 0 || hsUnconfirmed) return {
    level: 'conditional', label: 'มีเงื่อนไข', color: 'var(--c-permit)',
    reason: measures.length
      ? `ต้องขอ/แจ้ง ${measures.length} รายการก่อนนำเข้า`
      : 'พิกัดยังไม่ยืนยัน — ควรขอ Advance Ruling ก่อนสั่ง',
  };
  return {
    // ⚠ เดิมขึ้น "ผ่าน" — กฎ 160 แถวมาจากทะเบียนที่ไม่เคยไล่มาตรการ (3924.10.99 ก็เคยว่างจน NSW เตือนจริง) ไม่พบ ≠ ไม่ต้องขอ
    level: 'pass', label: 'ไม่พบข้อกำหนดในระบบ', color: 'var(--muted)',
    reason: 'ยังไม่พบมาตรการของพิกัดนี้ — แปลว่ายังไม่มีข้อมูล ไม่ได้ยืนยันว่าไม่ต้องขอ ฝากช่วยถามชิปปิ้งก่อนสั่ง',
  };
}

export function permitRows(rule) {
  const measures = Array.isArray(rule?.measures) ? rule.measures : [];
  return measures.map(m => ({
    reqType: m.req_type,
    label: reqLabel(m.req_type),
    cat: reqCat(m.req_type),
    side: m.side,
    sideLabel: m.side === 'export' ? 'ต้นทางส่ง' : m.side === 'import' ? 'ขอในไทย' : '',
    neededBy: m.needed_by,
    remindBefore: m.remind_before,
    note: m.note,
    agency: m.agency ? measureAgencies(m).map(a => PERMIT_AGENCY[a].name).join(' · ') : (AGENCY_TH[agencyFromReq(m.req_type)] ?? null),
  }));
}

function agencyFromReq(rt) {
  if (rt === 'permit_forest') return 'forest_dept';
  if (rt === 'permit_tisi') return 'tisi';
  if (rt === 'permit_fda') return 'fda';
  if (rt === 'permit_excise') return 'excise';
  if (rt === 'phytosanitary' || rt === 'fumigation') return 'other';
  return 'other';
}

// แนะนำพิกัดจากคำอธิบายสินค้า (keyword match กับ description_th/notes)
// เฟสแรก: จับคำ — ไม่ใช่ AI; คนต้องยืนยัน/ให้ broker ตรวจอยู่ดี
const STOP = new Set(['และ', 'หรือ', 'ของ', 'ที่', 'ใช้', 'แบบ', 'สำหรับ', 'ชนิด', 'the', 'for', 'of', 'and']);
export function suggestHs(desc, rules) {
  const words = (desc || '').toLowerCase()
    .split(/[\s,/·()]+/).map(w => w.trim())
    .filter(w => w.length >= 2 && !STOP.has(w));
  if (!words.length) return [];
  const scored = rules.map(r => {
    const hay = `${r.description_th ?? ''} ${r.notes ?? ''}`.toLowerCase();
    let score = 0;
    for (const w of words) if (hay.includes(w)) score += (w.length >= 4 ? 2 : 1);
    return { rule: r, score };
  }).filter(x => x.score > 0);
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, 4);
}

// จับคำกับคลังสินค้าที่เคยนำเข้า (ยืนยัน HS แล้ว) — แม่นสุด เพราะ broker เคยตีจริง
export function suggestFromProducts(desc, classifications) {
  const words = (desc || '').toLowerCase()
    .split(/[\s,/·()]+/).map(w => w.trim()).filter(w => w.length >= 2 && !STOP.has(w));
  if (!words.length) return [];
  const scored = classifications
    .filter(c => c.hs_code && c.hs_confirmed)
    .map(c => {
      const hay = `${c.name_th ?? ''} ${c.name_en ?? ''} ${c.sku ?? ''} ${c.what_is_it ?? ''} ${c.material ?? ''}`.toLowerCase();
      let score = 0;
      for (const w of words) if (hay.includes(w)) score += (w.length >= 4 ? 2 : 1);
      return { product: c, score };
    }).filter(x => x.score > 0);
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, 3);
}

// rule ควร re-verify ถ้า last_verified ว่าง หรือเก่ากว่า 90 วัน
export function staleRule(rule) {
  if (!rule?.last_verified) return { stale: true, days: null };
  const days = Math.round((Date.now() - new Date(rule.last_verified)) / 86400000);
  return { stale: days > 90, days };
}
